/**
 * Batch Processes — Progress Selectors (TV1-1979)
 *
 * PURE functions of `( process, projectedCount )` — no store-slice coupling —
 * so both `batchProcesses/detail` (one process) and `batchProcesses/shared`
 * (N processes) share one implementation.
 *
 * `projectedCount` is the success count derived from the `batch-progress`
 * readmodel (rows already visible to the user). `null` means it has not loaded
 * yet, in which case success falls back to the write-side
 * `completedCount - errorCount`.
 *
 * `effectiveTotal = totalItems - noOpCount - skippedUnresolvedCount`. Both are
 * SKIPPED units that will never produce a batch-progress row:
 *   - `noOpCount` — attempted but already at the right value (idempotency /
 *     empty-diff skip);
 *   - `skippedUnresolvedCount` — never attempted, no resolvable identity
 *     (ERP-ONBOARDING O13).
 * Factoring both out guarantees the bar converges to 100% once projections catch
 * up — no clock, timeout, or fallback. Leaving either IN is what makes a CORRECT
 * re-run of an ERP import (where the equality damper legitimately drops every row)
 * read as a run stalled at 0%.
 */

import { BatchProcessesReadmodel } from '@bett3r-dev/teselly-domain';

/**
 * The FE-consumed batch-process readmodel plus the completion-carried fields
 * (`successCount` / `errorCount` / `noOpCount`) which arrive via
 * `BatchProcessCompleted`. They are optional here because the
 * readmodel schema declares them as such (or not yet at all — see slice 6/7).
 */
export type ProgressProcess = BatchProcessesReadmodel & {
  successCount?: number;
  errorCount?: number;
  noOpCount?: number;
  skippedUnresolvedCount?: number;
};

/**
 * Units that will never produce a batch-progress row, so the reachable total is
 * `totalItems` minus every SKIP bucket.
 */
const effectiveTotal = ( process: ProgressProcess ): number =>
  process.totalItems - ( process.noOpCount ?? 0 ) - ( process.skippedUnresolvedCount ?? 0 );

/**
 * Number of errored units. Prefers the authoritative `errorCount` from the
 * Completed event over the fire-and-forget id array.
 */
export const errorCount = ( process: ProgressProcess | null ): number => {
  if ( !process ) return 0;
  if ( process.errorCount != null ) return process.errorCount;
  return process.erroredTargetIds?.length ?? 0;
};

/**
 * Successfully visible units, from the `batch-progress` readmodel. Falls back
 * to the write-side `completedCount - errorCount` while `projectedCount` is
 * still loading, so the bar does not regress to 0% on first paint.
 */
export const successCount = (
  process: ProgressProcess | null,
  projectedCount: number | null
): number => {
  if ( projectedCount != null ) return projectedCount;
  if ( !process ) return 0;
  if ( process.successCount != null ) return process.successCount;
  const completed = process.completedCount ?? 0;
  const errored = errorCount( process );
  return Math.max( 0, completed - errored );
};

/**
 * Progress percentage (0-100), based on
 * `( successCount + errorCount ) / effectiveTotal`. Never exceeds 100.
 */
export const progressPercentage = (
  process: ProgressProcess | null,
  projectedCount: number | null
): number => {
  if ( !process ) return 0;
  const total = effectiveTotal( process );
  // Every unit no-op'd (nothing reachable): a completed batch is 100% done, not
  // 0% — otherwise the bar flashes empty for a batch that had nothing to do.
  if ( total <= 0 ) return process.status === 'completed' ? 100 : 0;
  const success = successCount( process, projectedCount );
  const errors = errorCount( process );
  return Math.min( 100, Math.round((( success + errors ) / total ) * 100 ));
};

/**
 * "Finalizing" affordance: the process reached a terminal write-side state
 * (completed / canceled) but the user-visible projections have not fully caught
 * up against `effectiveTotal` yet.
 */
export const isFinalizing = (
  process: ProgressProcess | null,
  projectedCount: number | null
): boolean => {
  if ( !process ) return false;
  const { status } = process;
  if ( status !== 'completed' && status !== 'canceled' ) return false;
  const success = successCount( process, projectedCount );
  const errors = errorCount( process );
  return ( success + errors ) < effectiveTotal( process );
};

/**
 * Uniform auto-removal criterion (TV1-1979 §2.6): a process is auto-removed from
 * the active list ONLY when it cleanly completed AND its user-visible projections
 * have fully converged. Errored and canceled batches keep requiring manual
 * archive — the user must be able to see what happened / retry.
 */
export const shouldAutoRemoveProcess = (
  process: ProgressProcess | null,
  projectedCount: number | null
): boolean => {
  if ( !process ) return false;
  return process.status === 'completed'
    && errorCount( process ) === 0
    && !isFinalizing( process, projectedCount );
};
